/**
 * KsiValidationService — V96-3-2 / V96-3-3 / ADR-0045 §3–4.
 *
 * Persistent validation for FedRAMP CR26 profiles. For every KSI in the
 * profile's class:
 *   - cadence: machine-evidenced KSIs use VDR-TFR-MVX (20x) / VDR-TFR-MVF
 *     (Rev5) for the class; attested ones use VDR-TFR-NMV. Numbers come
 *     from certification_timeframes, never code.
 *   - when due, record a ksi_validation_runs row from graph readiness. A
 *     `met` requirement whose evidence is older than its cadence is not met
 *     (stale) — persistent validation means *current* evidence.
 *   - not met → open/refresh a `ksi_validation_failure` finding (CR26 treats
 *     a failed validation as a vulnerability, so its deadline is a VDR
 *     deadline); met → auto-close it.
 *   - report which connectors must re-run to keep machine cadence.
 * If the class cadence is not available (rules batch not approved) the run
 * reports `blocked` rather than silently skipping validation.
 *
 * V103-6-5 / ADR-0068 §3: every run also writes `machine` determinations for
 * the machine-validated KSI objectives (ksi-machine-validation-service). The
 * readiness switch (`fedramp-readiness` rule set) decides what drives the
 * ledger and findings:
 *   - SHADOW (engine `ksi`, before the switch): the legacy graph readiness
 *     above, unchanged; machine rows are written alongside so both engines can
 *     be compared on real data.
 *   - OBJECTIVE (after the switch): one ledger row per due KSI from its
 *     objective rollup (`met` → met; anything else → gap, the reason naming
 *     not_met / no_evidence / undetermined). `ksi_validation_failure` (VDR
 *     deadline) opens only on not_met; a LAPSED machine validation
 *     (no_evidence) opens a `ksi_validation_lapse` finding with no VDR deadline
 *     that closes on the next passing run (owner decision 2). Overdue person /
 *     assessor determinations are flagged only (owner decision 3). No open
 *     period → `blocked` (the Operate panel offers "Start period").
 * Metric history stays continuous: the ledger keeps one row per KSI per run.
 * Every outcome cites the cadence rules it used (id, version, verification),
 * and each finding's detail carries its deadline provenance (V98-0-4).
 */
import { programHas } from '../programs/manifests.js';
import { postureCheckKinds } from '../connectors/manifests.js';
import { FRAMEWORK_IDS } from '../data/framework-ids.js';
import { SECURITY_BASELINE_CONTROLS } from '../data/security-baseline.js';
import { addTimeframe, lookupTimeframe, timeframeProvenance, vulnerabilityDeadline, } from './certification-service.js';
import { computeControlStates, computeFrameworkReadiness } from './requirement-readiness-service.js';
import { resolveReadinessEngine } from './program-rule-set-service.js';
import { SLA_HOURS } from './finding-service.js';
import { runMachineValidation } from './ksi-machine-validation-service.js';
import { buildFedrampKsiObjectiveView } from './fedramp-ksi-service.js';
import { syncFedrampClockFindings } from './fedramp-clocks-service.js';
const MODE = new Map(SECURITY_BASELINE_CONTROLS.map((c) => [c.id, c.mode]));
async function cadences(db, profile) {
    const path = profile.path ?? '20x';
    const machineRule = path === '20x' ? 'VDR-TFR-MVX' : 'VDR-TFR-MVF';
    return {
        machineRule,
        machine: await lookupTimeframe(db, machineRule, { path, certificationClass: profile.certification_class, pain: null, variant: null }),
        nonMachine: await lookupTimeframe(db, 'VDR-TFR-NMV', { path, certificationClass: profile.certification_class, pain: null, variant: null }),
    };
}
export async function runKsiValidation(db, profile, now, opts) {
    const content = opts.contentDb ?? db;
    const out = {
        profile_id: profile.profile_id, status: 'ok', validated: 0, not_met: 0, findings_opened: 0, findings_closed: 0, connectors_due: [],
    };
    if (!programHas(profile.program, (m) => !!m.features.persistentValidation) || profile.status !== 'active')
        return out;
    const cad = await cadences(content, profile);
    out.cadence_provenance = {
        machine: timeframeProvenance(cad.machineRule, cad.machine),
        non_machine: timeframeProvenance('VDR-TFR-NMV', cad.nonMachine),
    };
    if (!cad.machine || !cad.nonMachine) {
        return { ...out, status: 'blocked', blocked_reason: 'FedRAMP validation cadences are not approved yet — approve the FedRAMP rules batch in the review queue.' };
    }
    const engine = await resolveReadinessEngine(content, profile.program);
    out.engine = engine.engine === 'objective' ? 'objective' : 'ksi';
    out.mode = out.engine === 'objective' ? 'objective' : 'shadow';
    out.machine = await runMachineValidation(db, profile, now, { cadence: cad.machine, contentDb: content });
    if (out.mode === 'objective') {
        await objectiveLedger(db, profile, now, { ...opts, cad, out });
        out.connectors_due = await connectorsDue(db, profile, now, cad.machine);
        return out;
    }
    const readiness = await computeFrameworkReadiness(db, profile.workspace_id, FRAMEWORK_IDS.FEDRAMP_CR26, {
        certificationClass: profile.certification_class, now, contentDb: content, profileId: profile.profile_id,
    });
    const allControls = [...new Set(readiness.requirements.flatMap((r) => r.crediting_controls.map((c) => c.control_id)))];
    const evidence = await computeControlStates(db, profile.workspace_id, allControls, { now, contentDb: content, profileId: profile.profile_id });
    const last = await db.query(`SELECT requirement_id, MAX(completed_at) AS completed_at FROM ksi_validation_runs WHERE profile_id = ? AND workspace_id = ? GROUP BY requirement_id`, [profile.profile_id, profile.workspace_id]);
    const lastRun = new Map(last.results.map((r) => [r.requirement_id, r.completed_at]));
    for (const req of readiness.requirements) {
        const machine = req.crediting_controls.some((c) => MODE.get(c.control_id) === 'machine');
        const cadence = machine ? cad.machine : cad.nonMachine;
        const prev = lastRun.get(req.requirement_id);
        if (prev && addTimeframe(new Date(prev), cadence.timeframe_num, cadence.timeframe_type) > now)
            continue;
        // Persistent validation needs evidence newer than one cadence window.
        let status = req.status;
        let reason = req.reason;
        if (status === 'met') {
            const stale = req.crediting_controls.filter((c) => {
                const seen = evidence.get(c.control_id)?.observed_at;
                return !seen || addTimeframe(new Date(seen), cadence.timeframe_num, cadence.timeframe_type) <= now;
            });
            if (stale.length) {
                status = 'gap';
                reason = `Evidence older than the ${cadence.timeframe_num}-${cadence.timeframe_type} ${machine ? 'machine' : 'non-machine'} validation cadence: ${stale.map((s) => s.control_id).join(', ')}.`;
            }
        }
        const due = prev ? addTimeframe(new Date(prev), cadence.timeframe_num, cadence.timeframe_type) : now;
        await db.execute(`INSERT INTO ksi_validation_runs (run_id, workspace_id, profile_id, requirement_id, validation_type, status, reason, evidence_check_ids_json, due_at, completed_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [
            opts.newId(), profile.workspace_id, profile.profile_id, req.requirement_id, machine ? 'machine' : 'non_machine',
            status, reason,
            JSON.stringify(req.crediting_controls.map((c) => evidence.get(c.control_id)?.last_check_id).filter(Boolean)),
            due.toISOString(), now.toISOString(),
        ]);
        out.validated++;
        const dedup = `ksi:${profile.profile_id}:${req.requirement_id}`;
        if (status === 'met') {
            const r = await db.execute(`UPDATE findings SET status = 'auto_closed', closed_at = ?, updated_at = ?
          WHERE workspace_id = ? AND dedup_key = ? AND status IN ('open', 'acknowledged')`, [now.toISOString(), now.toISOString(), profile.workspace_id, dedup]);
            out.findings_closed += r.meta?.changes ?? 0;
            continue;
        }
        out.not_met++;
        const deadline = await vulnerabilityDeadline(content, profile, {
            severity: 'high', first_seen: now.toISOString(), kev: false, internet_reachable: null,
        }, now);
        const detail = JSON.stringify({ requirement: req.ref, title: req.title, status, reason, deadline_basis: deadline.basis, deadline_provenance: deadline.provenance });
        const ins = await db.execute(`INSERT INTO findings (finding_id, workspace_id, control_id, kind, status, detail, dedup_key, severity, sla_hours, sla_deadline_at,
                             deadline_policy, pain_rating, vdr_exposure, certification_program, opened_at, updated_at)
       SELECT ?, ?, ?, 'ksi_validation_failure', 'open', ?, ?, 'high', ?, ?, ?, ?, ?, ?, ?, ?
        WHERE NOT EXISTS (SELECT 1 FROM findings WHERE workspace_id = ? AND dedup_key = ? AND status IN ('open', 'acknowledged'))`, [
            opts.newId(), profile.workspace_id, req.requirement_id, detail, dedup,
            Math.max(1, Math.round((deadline.deadline.getTime() - now.getTime()) / 3_600_000)), deadline.deadline.toISOString(),
            deadline.policy, deadline.pain != null ? String(deadline.pain) : null, deadline.exposure, profile.program,
            now.toISOString(), now.toISOString(), profile.workspace_id, dedup,
        ]);
        if ((ins.meta?.changes ?? 0) > 0) {
            out.findings_opened++;
        }
        else {
            // Keep the open finding's reason current; never extend its deadline.
            await db.execute(`UPDATE findings SET detail = ?, updated_at = ? WHERE workspace_id = ? AND dedup_key = ? AND status IN ('open', 'acknowledged')`, [detail, now.toISOString(), profile.workspace_id, dedup]);
        }
    }
    out.connectors_due = await connectorsDue(db, profile, now, cad.machine);
    return out;
}
/** Machine cadence → which posture connectors must re-collect. */
async function connectorsDue(db, profile, now, machine) {
    // ADR-0051: the posture-check kinds come from the connector manifest.
    // d1-in-list: bounded — a static registry-derived list (< 20 kinds).
    const postureKinds = postureCheckKinds();
    const conns = await db.query(`SELECT connector_id, last_run_at FROM evidence_connectors
      WHERE workspace_id = ? AND status = 'active'
        AND kind IN (${postureKinds.map(() => '?').join(', ')})`, [profile.workspace_id, ...postureKinds]);
    return conns.results
        .filter((c) => !c.last_run_at || addTimeframe(new Date(c.last_run_at), machine.timeframe_num, machine.timeframe_type) <= addTimeframe(now, 1, 'days'))
        .map((c) => c.connector_id);
}
/**
 * The dedup key a new finding should use: null while one is live (open /
 * acknowledged), else the key, or `key#N` once earlier generations closed
 * (`idx_findings_dedup` is unique per workspace across every status).
 */
export async function nextFindingDedupKey(db, workspaceId, key) {
    const rows = (await db.query(`SELECT dedup_key, status FROM findings WHERE workspace_id = ? AND (dedup_key = ? OR substr(dedup_key, 1, ?) = ?)`, [workspaceId, key, key.length + 1, `${key}#`])).results;
    if (rows.some((r) => r.status === 'open' || r.status === 'acknowledged'))
        return null;
    return rows.length ? `${key}#${rows.length + 1}` : key;
}
/** Auto-close every live generation of a finding key. */
export async function closeFindingGenerations(db, workspaceId, key, at) {
    const r = await db.execute(`UPDATE findings SET status = 'auto_closed', closed_at = ?, updated_at = ?
      WHERE workspace_id = ? AND (dedup_key = ? OR substr(dedup_key, 1, ?) = ?) AND status IN ('open', 'acknowledged')`, [at, at, workspaceId, key, key.length + 1, `${key}#`]);
    return r.meta?.changes ?? 0;
}
/** Dedup key of a KSI's lapse finding (ADR-0068 owner decision 2). */
export const ksiLapseKey = (profileId, requirementId) => `ksi-lapse:${profileId}:${requirementId}`;
/** Why a KSI's objective rollup is not met, naming each objective's state. */
function rollupReason(row) {
    if (row.status === 'met')
        return 'Every KSI objective is met.';
    const of = (s) => row.objectives.filter((o) => o.status === s).map((o) => o.label);
    const parts = ['not_met', 'no_evidence', 'undetermined'].map((s) => [s, of(s)]).filter(([, l]) => l.length).map(([s, l]) => `${s}: ${l.join(', ')}`);
    return `KSI objectives ${row.status} (${parts.join('; ')}).`;
}
/**
 * OBJECTIVE mode (after the switch): the ledger from each due KSI's objective
 * rollup; not_met → ksi_validation_failure (VDR deadline); a lapsed machine
 * validation → ksi_validation_lapse (no VDR deadline), closed once it passes.
 */
async function objectiveLedger(db, profile, now, ctx) {
    const content = ctx.contentDb ?? db;
    const out = ctx.out;
    const view = await buildFedrampKsiObjectiveView(db, profile.workspace_id, profile.profile_id, { now, contentDb: content });
    out.overdue_flags = view.overdue;
    if (!view.period || !(out.machine && out.machine.status !== 'no_open_period')) {
        out.status = 'blocked';
        out.blocked_reason = 'No open period: persistent validation records its results in a planned or fieldwork period. Start a period.';
        return;
    }
    const last = await db.query(`SELECT requirement_id, MAX(completed_at) AS completed_at FROM ksi_validation_runs WHERE profile_id = ? AND workspace_id = ? GROUP BY requirement_id`, [profile.profile_id, profile.workspace_id]);
    const lastRun = new Map(last.results.map((r) => [r.requirement_id, r.completed_at]));
    const iso = now.toISOString();
    out.lapse_opened = 0;
    out.lapse_closed = 0;
    for (const row of view.rows) {
        // Lapse (every run): open while a machine-validated objective reads no_evidence; close on the next passing run.
        const lapseKey = ksiLapseKey(profile.profile_id, row.requirement_id);
        if (row.lapsed) {
            const key = await nextFindingDedupKey(db, profile.workspace_id, lapseKey);
            if (key) {
                await db.execute(`INSERT INTO findings (finding_id, workspace_id, control_id, kind, status, detail, dedup_key, severity, sla_hours, sla_deadline_at,
                                 deadline_policy, certification_program, opened_at, updated_at)
           VALUES (?, ?, ?, 'ksi_validation_lapse', 'open', ?, ?, 'medium', ?, ?, 'ops_sla', ?, ?, ?)`, [
                    ctx.newId(), profile.workspace_id, row.requirement_id,
                    JSON.stringify({ requirement: row.ref, title: row.title, lapsed: row.objectives.filter((o) => o.lapsed).map((o) => o.label), action: 'Collect or connect the evidence persistent validation reads.' }),
                    key, SLA_HOURS.medium, addTimeframe(now, SLA_HOURS.medium, 'hours').toISOString(), profile.program, iso, iso,
                ]);
                out.lapse_opened++;
            }
        }
        else {
            out.lapse_closed += await closeFindingGenerations(db, profile.workspace_id, lapseKey, iso);
        }
        const machine = row.objectives.some((o) => o.machine_validated);
        const cadence = machine ? ctx.cad.machine : ctx.cad.nonMachine;
        const prev = lastRun.get(row.requirement_id);
        if (prev && addTimeframe(new Date(prev), cadence.timeframe_num, cadence.timeframe_type) > now)
            continue;
        const status = row.status === 'met' ? 'met' : 'gap';
        const reason = rollupReason(row);
        const due = prev ? addTimeframe(new Date(prev), cadence.timeframe_num, cadence.timeframe_type) : now;
        await db.execute(`INSERT INTO ksi_validation_runs (run_id, workspace_id, profile_id, requirement_id, validation_type, status, reason, evidence_check_ids_json, due_at, completed_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [ctx.newId(), profile.workspace_id, profile.profile_id, row.requirement_id, machine ? 'machine' : 'non_machine', status, reason,
            JSON.stringify(row.objectives.flatMap((o) => (o.sources?.machine ? [o.sources.machine.determination_id] : []))), due.toISOString(), iso]);
        out.validated++;
        const dedup = `ksi:${profile.profile_id}:${row.requirement_id}`;
        if (row.status === 'met') {
            out.findings_closed += await closeFindingGenerations(db, profile.workspace_id, dedup, iso);
            continue;
        }
        if (row.status !== 'not_met')
            continue; // no_evidence / undetermined: no vulnerability (ADR-0068 §3)
        out.not_met++;
        const deadline = await vulnerabilityDeadline(content, profile, { severity: 'high', first_seen: iso, kev: false, internet_reachable: null }, now);
        const detail = JSON.stringify({ requirement: row.ref, title: row.title, status: 'not_met', reason, deadline_basis: deadline.basis, deadline_provenance: deadline.provenance });
        const key = await nextFindingDedupKey(db, profile.workspace_id, dedup);
        if (key) {
            await db.execute(`INSERT INTO findings (finding_id, workspace_id, control_id, kind, status, detail, dedup_key, severity, sla_hours, sla_deadline_at,
                               deadline_policy, pain_rating, vdr_exposure, certification_program, opened_at, updated_at)
         VALUES (?, ?, ?, 'ksi_validation_failure', 'open', ?, ?, 'high', ?, ?, ?, ?, ?, ?, ?, ?)`, [
                ctx.newId(), profile.workspace_id, row.requirement_id, detail, key,
                Math.max(1, Math.round((deadline.deadline.getTime() - now.getTime()) / 3_600_000)), deadline.deadline.toISOString(),
                deadline.policy, deadline.pain != null ? String(deadline.pain) : null, deadline.exposure, profile.program, iso, iso,
            ]);
            out.findings_opened++;
        }
        else {
            // Keep the open finding's reason current; never extend its deadline.
            await db.execute(`UPDATE findings SET detail = ?, updated_at = ? WHERE workspace_id = ? AND (dedup_key = ? OR substr(dedup_key, 1, ?) = ?) AND status IN ('open', 'acknowledged')`, [detail, iso, profile.workspace_id, dedup, dedup.length + 1, `${dedup}#`]);
        }
    } // ADR-0068 §7: IVV-CSX-AIA annual assessment and CPO-CSX-CPM maintenance clocks (Operate findings, never VDR).
    const clocks = await syncFedrampClockFindings(db, profile, now, { newId: ctx.newId, contentDb: content });
    out.clock_findings_opened = clocks.opened;
    out.clock_findings_closed = clocks.closed;
}
/** Hourly sweep entry: every active CR26 profile across workspaces. */
export async function runCertificationSweep(db, now, opts) {
    const profiles = await db.query(`SELECT * FROM certification_profiles WHERE status = 'active' AND program = ?`, [FRAMEWORK_IDS.FEDRAMP_CR26]);
    const outcomes = [];
    for (const p of profiles.results) {
        try {
            outcomes.push(await runKsiValidation(db, p, now, opts));
        }
        catch (err) {
            outcomes.push({ profile_id: p.profile_id, status: 'blocked', blocked_reason: err.message, validated: 0, not_met: 0, findings_opened: 0, findings_closed: 0, connectors_due: [] });
        }
    }
    return { profiles: profiles.results.length, outcomes };
}
//# sourceMappingURL=ksi-validation-service.js.map